import { useEffect, useState } from 'react';
import { SimplePaletteColorOptions } from '@mui/material';
import { ApplicationErrorType, CustomErrorAction, ErrorDetails } from './types';
import { useAppSelector } from '../../store';
import { CompanySettingsMap } from '../../Configuration/CompanySettingsMap';
import ApplicationErrorAction from './ApplicationErrorAction';
import { AppSettings } from '../../Configuration/types';
import MailLink from '@components/MailLink';
import PhoneLink from '@components/PhoneLink';

import styles from './ApplicationError.module.css';

interface ApplicationErrorProps {
	type: ApplicationErrorType;
	/** Used for dynamic error messages that need an injected value */
	messageParam?: string;
	/** Used for consumer defining the available action upon error */
	customAction?: CustomErrorAction;
}

/**
 * Component for rendering friendly error pages and actions depending on the type of error passed in.
 */
export default function ApplicationError(props: ApplicationErrorProps) {
	let company: string | undefined;

	try {
		// It is possible the error occurred at a point where we could not retrieve app settings and do not know the company
		const appSettings = useAppSelector((state) => state.appSettings as AppSettings);
		company = appSettings.company;
	} catch {
		company = findCompanyByCurrentLocation();
	}

	// If for some reason we could not determine the company, return a simplified error. This shouldn't happen hypothetically.
	if (!company) {
		return <RenderError type={props.type} />;
	}

	const { domain: companyDomain, supportPhoneNumber, supportExtension, theme } = CompanySettingsMap[company];
	const brandColor: SimplePaletteColorOptions | undefined = theme.palette?.primary as SimplePaletteColorOptions;

	return (
		<RenderError
			type={props.type}
			companyDomain={companyDomain}
			supportNumber={supportPhoneNumber}
			supportExtension={supportExtension}
			brandColor={brandColor.main}
			messageParam={props.messageParam}
			customAction={props.customAction}
		/>
	);
}

function RenderError(props: {
	type: ApplicationErrorType;
	companyDomain?: string;
	supportNumber?: string;
	supportExtension?: string;
	brandColor?: string;
	messageParam?: string;
	customAction?: CustomErrorAction;
}) {
	const errorDetails = new ErrorDetails(props.type, props.messageParam, props.customAction);
	const [isMobile, setIsMobile] = useState(window.screen.width < 600 ? true : false);

	useEffect(() => {
		const resizeFunc = () => {
			if (window.screen.width < 600) {
				setIsMobile(true);
			} else {
				setIsMobile(false);
			}
		};

		window.addEventListener('resize', resizeFunc);

		return () => {
			window.removeEventListener('resize', resizeFunc);
		};
	}, []);

	return (
		<div className={styles.root}>
			<span className={styles.title}>{errorDetails.title}</span>

			<div className={styles.logo}>
				<img
					src={isMobile ? '/Company-Logo-Large.png' : '/Company-Logo-X-Large.png'}
					alt='Company/Brand Logo'
				/>
			</div>

			<span className={styles.text}>{errorDetails.message}</span>

			{errorDetails.useContactSupportMessage && props.companyDomain && props.supportNumber && (
				<div className={styles.text}>
					<span>Please contact support at </span>
					<MailLink emailAddress={`helpdesk@${props.companyDomain}.com`} />
					<span> or </span>
					<PhoneLink
						phoneNumber={props.supportNumber}
						extension={props.supportExtension}
					/>
				</div>
			)}

			{errorDetails.action && (
				<div className={styles.action}>
					<ApplicationErrorAction
						errorDetails={errorDetails}
						actionColor={props.brandColor ?? '#000000'}
					/>
				</div>
			)}
		</div>
	);
}

function findCompanyByCurrentLocation() {
	const currentDomain = location.hostname;

	for (const company in CompanySettingsMap) {
		if (currentDomain.includes(CompanySettingsMap[company].domain)) {
			return company;
		}
	}

	// When testing locally we will not be able to match on domain, uncomment this code if you are testing this feature
	//if (currentDomain === 'localhost') {
	//	return "Horizon";
	//}

	// Could not determine the company
	return undefined;
}
